import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../../shared/constant";
import { toast } from "react-toastify";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import Loader from "../../shared/loader";
import Pagination from "../../shared/pagination";
import { isPWA, isMobilePWA, getPWADisplayMode } from "../../utils/pwa";
import { isWebView } from "../../utils/webview";
import "../../styles/pwa-customers.css";

const MyProduct = () => {
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sizeDrp, setSizeDrpData] = useState([]);
  const [seriesDrp, setSeriesDrp] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [productCount, setProductCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const debounceTimeout = useRef(null);
  const itemsPerPage = 30;
  const [pwaMode, setPwaMode] = useState(false);
  const [mobilePWA, setMobilePWA] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [refreshing, setRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [webViewMode, setWebViewMode] = useState(false);

  const [formData, setFormData] = useState({
    size_id: null,
    series_id: null,
  });

  const getProduct = (page = 1) => {
    setLoading(true);
    axios
      .post(API_URL.MY_PRODUCT_LIST, {
        name: searchText,
        series_id: formData.series_id,
        size_id: formData.size_id,
        page: page,
        limit: itemsPerPage,
      })
      .then(
        (res) => {
          if (res.data.status === 1) {
            setProductData(res.data.data.data);
            const total = res.data.data.total || 0;
            setTotalPages(Math.ceil(total / itemsPerPage));
            setProductCount(total);
          }
          setIsLoaded(true);
          setLoading(false);
        },
        () => {
          setLoading(false);
          toast.error("Something went wrong..", { autoClose: 3000 });
        }
      );
  };

  const getSizeDrpData = () => {
    axios.post(API_URL.SIZE_LIST, {}).then((res) => {
      let cd = res.data.data.map((caw) => ({
        label: caw.name,
        value: caw.id,
      }));
      setSizeDrpData(cd);
    });
  };
  
  const getSeries = () => {
    axios.post(API_URL.SERIES_LIST, {}).then((res) => {
      let cd = res.data.data.map((caw) => ({
        label: caw.name,
        value: caw.id,
      }));
      setSeriesDrp(cd);
    });
  };

  const deletProduct = (row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this - " + row.name,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios.post(API_URL.MY_PRODUCT_DELETE, { id: row.id }).then(
          (res) => {
            if (res.data.status === 1) {
              toast.success(row.name + " deleted successfully", { autoClose: 3000 });
              getProduct(currentPage); // reload current page
            }
            setLoading(false);
          },
          () => {
            setLoading(false);
            toast.error("Something went wrong..", { autoClose: 3000 });
          }
        );
      }
    });
  };

  const styles = {
    menu: (baseStyles) => ({
      ...baseStyles,
      marginTop: 2,
      zIndex: 999,
    }),
  };

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (pwaMode) {
        toast.success("Connection restored", { autoClose: 2000 });
        getProduct(currentPage); // Refresh data when back online
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      if (pwaMode) {
        toast.warning("You're offline. Some features may not work.", { autoClose: 3000 });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pwaMode, currentPage]);

  // Pull to refresh functionality
  useEffect(() => {
    if (!pwaMode || !mobilePWA) return;

    let startY = 0;
    let currentY = 0;
    let isPulling = false;
    let pullElement = null;

    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        pullElement = document.querySelector('.pwa-product-list');
      }
    };

    const handleTouchMove = (e) => {
      if (window.scrollY === 0 && pullElement) {
        currentY = e.touches[0].clientY;
        const pullDistance = currentY - startY;

        if (pullDistance > 50) {
          isPulling = true;
          pullElement.style.transform = `translateY(${Math.min(pullDistance * 0.3, 50)}px)`;
          pullElement.style.opacity = '0.7';
        }
      }
    };

    const handleTouchEnd = () => {
      if (isPulling && pullElement) {
        pullElement.style.transform = '';
        pullElement.style.opacity = '';
        
        setRefreshing(true);
        getProduct(currentPage).finally(() => {
          setRefreshing(false);
          toast.success("Data refreshed", { autoClose: 1500 });
        });
        
        isPulling = false;
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pwaMode, mobilePWA, currentPage]);

  useEffect(() => {
    getProduct(currentPage);
    getSizeDrpData();
    getSeries();
    // Check PWA mode, mobile, and webview
    setPwaMode(isPWA());
    setMobilePWA(isMobilePWA());
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    setWebViewMode(isWebView());
  }, []);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (isLoaded) {
        setCurrentPage(1); 
        getProduct(1);
      }
    }, 500);

    return () => clearTimeout(debounceTimeout.current);
  }, [searchText]);

  useEffect(() => {
    if (isLoaded) {
      setCurrentPage(1); 
      getProduct(1);
    }
  }, [formData.size_id, formData.series_id]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getProduct(page);
  };

  return (
    <>
      {(pwaMode || (isMobile && !webViewMode)) && (
        <div className="pwa-header bg-primary text-white p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">
              <i className="simple-icon-basket me-2"></i>
              Products ({productCount})
            </h4>
            <div className="d-flex align-items-center gap-2">
              {!isOnline && (
                <div className="pwa-status-indicator bg-warning">
                  <i className="simple-icon-wifi me-1"></i>
                  Offline
                </div>
              )}
              {mobilePWA && (
                <div className="pwa-status-indicator">
                  <i className="simple-icon-screen-smartphone me-1"></i>
                  PWA
                </div>
              )}
              {refreshing && (
                <div className="pwa-status-indicator bg-info">
                  <i className="simple-icon-refresh me-1"></i>
                  Refreshing
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className={`row ${(pwaMode || (isMobile && !webViewMode)) ? 'pwa-content' : ''}`}>
        {!(pwaMode || (isMobile && !webViewMode)) && (
          <>
        <div className="col-2">
          <h1>
            Product <small className="me-2">({productCount})</small>
          </h1>
              {webViewMode && (
                <small className="text-muted">
                  <i className="simple-icon-screen-smartphone me-1"></i>
                  WebView Mode
                </small>
              )}
        </div>
        <div className="col-10">
          <div className="d-flex align-items-center justify-content-end">
            <div className="form-group mb-0 mr-2" style={{ width: 250 }}>
              <input type="text" className="form-control" placeholder="Search by name" value={searchText}  onChange={(e) => setSearchText(e.target.value)}/>
            </div>
            <div className="form-group mb-0 mr-2" style={{ width: 250 }}>
              <Select
                placeholder="Select Series"
                className="form-control-sm"
                style={{ border: "1px solid lightgray" }}
                options={seriesDrp}
                isClearable
                value={seriesDrp.find((r) => r.value == formData.series_id)}
                styles={styles}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    series_id: (e) ? e.value : null,
                  });
                }}
              />
            </div>
            <div className="form-group mb-0 mr-2" style={{ width: 250 }}>
              <Select
                placeholder="Select Size"
                className="form-control-sm"
                style={{ border: "1px solid lightgray" }}
                options={sizeDrp}
                isClearable
                value={sizeDrp.find((r) => r.value == formData.size_id)}
                styles={styles}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    size_id: (e) ? e.value : null,
                  });
                }}
              />
            </div>
            <Link to={"/my-products/add"} className="btn btn-primary">
                  <i className="simple-icon-plus me-2" aria-hidden="true"></i> Add Product
            </Link>
          </div>
        </div>
          </>
        )}
        
        {(pwaMode || (isMobile && !webViewMode)) && (
          <div className="col-12">
            <div className="pwa-search-section mb-3">
              <div className="row">
                <div className="col-12 mb-3">
                  <div className="input-group">
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className="form-control form-control-lg"
                      placeholder="Search products by name..."
                    />
                    <button className="btn btn-outline-secondary" type="button">
                      <i className="simple-icon-magnifier"></i>
                    </button>
                  </div>
                </div>
                <div className="col-6">
                  <Select
                    placeholder="Select Series"
                    className="form-control-sm"
                    options={seriesDrp}
                    isClearable
                    value={seriesDrp.find((r) => r.value == formData.series_id)}
                    styles={styles}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        series_id: (e) ? e.value : null,
                      });
                    }}
                  />
                </div>
                <div className="col-6">
                  <Select
                    placeholder="Select Size"
                    className="form-control-sm"
                    options={sizeDrp}
                    isClearable
                    value={sizeDrp.find((r) => r.value == formData.size_id)}
                    styles={styles}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        size_id: (e) ? e.value : null,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="col-sm-12">
          {(pwaMode || (isMobile && !webViewMode)) ? (
            // PWA Mobile Card Layout
            <div className="pwa-product-list">
              {pwaMode && mobilePWA && (
                <div className="pwa-pull-hint text-center text-muted mb-3">
                  <small>
                    <i className="simple-icon-arrow-down me-1"></i>
                    Pull down to refresh
                  </small>
                </div>
              )}
              {loading && <Loader />}
              {!loading && (
                <div>
                  {productData.map((row, key) => {
                    return (
                      <div key={key} className="pwa-product-card mb-3">
                        <div className="card">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-3">
                                <div
                                  className="pwa-product-image"
                                  style={{
                                    backgroundImage: "url(" + row.image + ")",
                                    height: 80,
                                    width: 80,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    borderRadius: "8px",
                                    margin: "0 auto"
                                  }}
                                ></div>
                              </div>
                              <div className="col-9">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <h6 className="card-title mb-1">{row.name}</h6>
                                    <small className="text-muted">#{(currentPage - 1) * itemsPerPage + key + 1}</small>
                                  </div>
                                  <div className="pwa-status-badge">
                                    <span className={`badge ${row.in_stock > 0 ? 'bg-success' : 'bg-danger'}`}>
                                      {row.in_stock > 0 ? 'In Stock' : 'Out of Stock'}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="pwa-product-details mb-3">
                                  <div className="row">
                                    <div className="col-6">
                                      <small className="text-muted d-block">Price</small>
                                      <small className="fw-bold text-primary">₹{row.price}</small>
                                    </div>
                                    <div className="col-6">
                                      <small className="text-muted d-block">Stock</small>
                                      <small>{row.in_stock} units</small>
                                    </div>
                                  </div>
                                  <div className="row mt-2">
                                    <div className="col-6">
                                      <small className="text-muted d-block">Category</small>
                                      <small>{row.category_name}</small>
                                    </div>
                                    <div className="col-6">
                                      <small className="text-muted d-block">Size</small>
                                      <small>{row.size_name}</small>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="pwa-actions">
                                  <div className="btn-group w-100" role="group">
                                    <Link 
                                      to={`/my-products/edit/${row.id}`} 
                                      className="btn btn-outline-primary btn-sm"
                                    >
                                      <i className="simple-icon-pencil me-1"></i> Edit
                                    </Link>
                                    <button 
                                      onClick={() => deletProduct(row)} 
                                      className="btn btn-outline-danger btn-sm"
                                    >
                                      <i className="simple-icon-trash me-1"></i> Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {productData.length === 0 && (
                    <div className="text-center py-5">
                      <i className="simple-icon-basket" style={{fontSize: '3rem'}}></i>
                      <h5>No products found</h5>
                      <p className="text-muted">Start by adding your first product</p>
                    </div>
                  )}
                  
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center my-3">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Original Desktop Table Layout
            <div className="card">
              <div className="card-body p-0">
                {loading && <Loader />}
                {!loading && (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Image</th>
                          <th>Name</th>
                          <th>Price</th>
                          <th>In Stock</th>
                          <th>Category</th>
                          <th>Size</th>
                          <th style={{ width: "300px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productData.map((row, key) => (
                          <tr key={key}>
                            <td>{(currentPage - 1) * itemsPerPage + key + 1}</td>
                            <td className="table-avatar">
                              <div
                                style={{
                                  backgroundImage: "url(" + row.image + ")",
                                  height: 50,
                                  width: 50,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                }}
                              ></div>
                            </td>
                            <td>{row.name}</td>
                            <td>{row.price}</td>
                            <td>{row.in_stock}</td>
                            <td>{row.category_name}</td>
                            <td>{row.size_name}</td>
                            <td className="d-flex">
                              <Link to={`/my-products/edit/${row.id}`} className="btn btn-sm btn-secondary mr-2">
                                <i className="simple-icon-pencil me-1"></i> Edit
                              </Link>
                              <button onClick={() => deletProduct(row)} className="btn btn-sm btn-danger">
                                <i className="simple-icon-trash me-1"></i> Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center my-3">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* PWA Floating Action Button */}
      {(pwaMode || (isMobile && !webViewMode)) && (
        <Link 
          to={"/my-products/add"} 
          className="btn btn-primary pwa-fab position-fixed rounded-circle shadow-lg"
          style={{
            bottom: '100px',
            right: '20px',
            width: '60px',
            height: '60px',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
        >
          <i className="simple-icon-plus"></i>
        </Link>
      )}
    </>
  );
};

export default MyProduct;
